import { bunja } from 'bunja';
import { createScopeFromContext, useBunja } from 'bunja/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useTranslations } from 'next-intl';
import { createContext, useMemo, useReducer, useRef, type KeyboardEvent, type Ref } from 'react';

import { LeverageBadge, OrderSideBadge } from '@flipster/features/src/misc/badge';
import { SymbolDisplay } from '@flipster/features/src/misc/display';
import { calcOrderEstUnrealizedPnl } from '@flipster/features/src/shared/calculator/tp-sl-preview';
import {
  onBasisModeChange,
  syncOnLimitPriceChange,
  syncOnSecondaryChange,
  syncOnTriggerPriceChange,
  targetPriceToBasisValue,
  type ModalBasisMode,
  type PriceBasisState,
  type TpslBasisContext,
} from '@flipster/features/src/shared/calculator/tpsl-basis';
import { formatTpslConvertedPrice } from '@flipster/features/src/shared/tpsl/format-converted-price';
import { ModalPriceBasisRow } from '@flipster/features/src/shared/tpsl/modal-price-basis-row';
import { formatModalSecondaryDisplay } from '@flipster/features/src/shared/tpsl/tpsl-basis-field/placeholders';
import {
  defaultAdvancedOrderTpslBasisModes,
  readAdvancedOrderTpslBasisModes,
  type AdvancedOrderTpslBasisModes,
} from '@flipster/features/src/shared/tpsl/tpsl-basis-persistence';
// ROLLBACK(last-price): trigger price type 셀렉터 재활성화 시 복원.
// import {
//   TriggerPriceTypeSelect,
//   defaultTriggerPriceType,
// } from '@flipster/features/src/shared/trigger-price-type-select';
import { ModalErrorBoundary } from '@flipster/features/src/utils/modal-error-boundary';
import { FormattedMessage } from '@flipster/i18n';
import { Button } from '@flipster/react/design-system/button';
import { Checkbox } from '@flipster/react/design-system/checkbox';
import { Modal } from '@flipster/react/design-system/modal/index';
import { createTabsComponent } from '@flipster/react/design-system/tabs';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { flex, hstack, vstack } from '@flipster/styled-system/patterns';
import Num from '@flipster/utils/num';

import {
  emptyPreset,
  isConfirmDisabled,
  isLegActive,
  isLegValid,
  type AdvancedTpSlPreset,
  type LegType,
  type TpSlLeg,
} from '../advanced-tpsl';
import { symbolBunja, useMarginType } from '../bunja/base';
import { formBunja } from '../bunja/form';
import { realtimeStateBunja } from '../bunja/price';

export const AdvancedTpSlDraftContext = createContext<AdvancedTpSlPreset>(emptyPreset('Long'));
const AdvancedTpSlDraftScope = createScopeFromContext(AdvancedTpSlDraftContext);

const advancedTpSlModalBunja = bunja(() => {
  const initialDraftFromContext = bunja.use(AdvancedTpSlDraftScope);
  return { initialDraftFromContext };
});

/**
 * Advanced TP/SL 설정 모달.
 * 모달은 transient draft(useReducer)를 편집하고, Confirm 시 bunja committed preset으로 반영한다.
 * 진입 시 draft 초기값은 [[AdvancedTpSlDraftContext]]로 주입받는다(직전 inline 입력값 prefill).
 * 탭 전환 시 직전 입력은 초기화된다(최근 탭만 유지).
 * entryRef는 live `calculationPriceAtom` (모든 주문 타입 동일 — TL: freeze/if-else 없음).
 */
interface AdvancedTpSlModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExit: () => void;
}

type PresetSide = AdvancedTpSlPreset['side'];
const Tabs = createTabsComponent<PresetSide>();

// 가격 입력 Tab 순회 순서. 각 입력은 ref로 등록되며, 미렌더(limit 미사용) 입력은 순회에서 제외된다.
type PriceInputKey = 'tpTrigger' | 'tpLimit' | 'slTrigger' | 'slLimit';
const PRICE_INPUT_ORDER: PriceInputKey[] = ['tpTrigger', 'tpLimit', 'slTrigger', 'slLimit'];

type DraftAction =
  | { kind: 'switchSide'; side: PresetSide; modes: AdvancedOrderTpslBasisModes }
  | { kind: 'patchLeg'; leg: LegType; patch: Partial<TpSlLeg> };

const draftReducer = (state: AdvancedTpSlPreset, action: DraftAction): AdvancedTpSlPreset => {
  switch (action.kind) {
    case 'switchSide':
      // 탭 전환 시 직전 입력 초기화 (최근 탭만 유지). basis mode는 해당 side persist 값으로 복원.
      return applyBasisModesToPreset(emptyPreset(action.side), action.modes);
    case 'patchLeg':
      return { ...state, [action.leg]: { ...state[action.leg], ...action.patch } };
  }
};

function formatSecondaryDisplay(value: string | null | undefined): string {
  return formatModalSecondaryDisplay(value);
}

function formatAbsolutePrice(price: string, pricePrecision: number): string {
  // Converted secondary → price: ROUND_DOWN. Direct price input is not formatted here.
  return formatTpslConvertedPrice(price, pricePrecision);
}

function applyBasisModesToPreset(preset: AdvancedTpSlPreset, modes: AdvancedOrderTpslBasisModes): AdvancedTpSlPreset {
  return {
    ...preset,
    tp: {
      ...preset.tp,
      triggerBasisMode: modes.tp.trigger,
      // Limit secondary is Change(%) only (no ROI/PnL dropdown).
      limitBasisMode: 'change',
      triggerSecondary: '',
      limitSecondary: '',
    },
    sl: {
      ...preset.sl,
      triggerBasisMode: modes.sl.trigger,
      limitBasisMode: 'change',
      triggerSecondary: '',
      limitSecondary: '',
    },
  };
}

function withForwardedSecondaries(leg: TpSlLeg, ctx: TpslBasisContext): TpSlLeg {
  return {
    ...leg,
    limitBasisMode: 'change',
    triggerSecondary: formatSecondaryDisplay(targetPriceToBasisValue(leg.triggerBasisMode, leg.triggerPrice, ctx)),
    limitSecondary: formatSecondaryDisplay(targetPriceToBasisValue('change', leg.limitPrice, ctx)),
  };
}

function buildOrderBasisCtx(
  side: PresetSide,
  entryRefPrice: string,
  amount: string,
  leverage: number,
): TpslBasisContext {
  return {
    side,
    entryRefPrice,
    qty: '',
    margin: '',
    formulaProfile: 'order',
    amount,
    leverage,
  };
}

function initDraft(
  initial: AdvancedTpSlPreset,
  entryRefPrice: string,
  amount: string,
  leverage: number,
): AdvancedTpSlPreset {
  const modes = readAdvancedOrderTpslBasisModes() ?? defaultAdvancedOrderTpslBasisModes;
  const withModes = applyBasisModesToPreset(initial, modes);
  const ctx = buildOrderBasisCtx(initial.side, entryRefPrice, amount, leverage);
  return {
    ...withModes,
    tp: withForwardedSecondaries(withModes.tp, ctx),
    sl: withForwardedSecondaries(withModes.sl, ctx),
  };
}

const AdvancedTpSlModal = ({ isOpen, onClose, onExit }: AdvancedTpSlModalProps) => (
  <Modal.Root isOpen={isOpen} onClose={onClose} onExit={onExit} size='small' mode='fullModal'>
    {/* 데이터 레이어(bunja/atom)에서 throw되면 모달을 자동으로 닫는다. */}
    <ModalErrorBoundary onError={onClose}>
      <AdvancedTpSlModalContent onClose={onClose} />
    </ModalErrorBoundary>
  </Modal.Root>
);

const AdvancedTpSlModalContent = ({ onClose }: { onClose: () => void }) => {
  const { symbolInfoAtom } = useBunja(symbolBunja);
  const { calculationPriceAtom, commitAdvancedTpSlAtom, orderStateAtom } = useBunja(formBunja);
  const { amountAtom } = useBunja(realtimeStateBunja);
  // 모달 진입 시 draft 초기값(prefill). opener가 Context로 주입한 값을 scope로 받는다.
  const { initialDraftFromContext } = useBunja(advancedTpSlModalBunja);
  const { pricePrecision, quoteCurrency } = useAtomValue(symbolInfoAtom);
  // 검증/basis/Est 기준가: live mid/last (Market/Limit/Trigger 동일 — no freeze).
  const entryRefPrice = useAtomValue(calculationPriceAtom);
  const commit = useSetAtom(commitAdvancedTpSlAtom);
  // 예상 P&L 계산용 주문 컨텍스트
  const { leverage } = useAtomValue(orderStateAtom);
  const amount = useAtomValue(amountAtom);

  const [draft, dispatch] = useReducer(draftReducer, null, () =>
    initDraft(initialDraftFromContext, entryRefPrice, amount, leverage),
  );
  const t = useTranslations();

  // 인라인 검증은 trigger price 방향만 본다 (live entry 기준).
  // secondary는 사용자 입력/가격 편집 시에만 갱신. live entry는 estimated(OrderPnlHint)만 따라간다.
  const confirmDisabled = isConfirmDisabled(draft, { anchor: entryRefPrice });

  const handleConfirm = () => {
    if (confirmDisabled) return;
    // Basis mode persist SoT: commitAdvancedTpSlAtom → persistOrderFormInlineBasisModes.
    commit(draft);
    onClose();
  };

  // 가격 입력 ref 레지스트리. 각 입력(ModalPriceBasisRow price)이 callback ref로 자신을 등록한다.
  const priceInputRefs = useRef<Partial<Record<PriceInputKey, HTMLInputElement | null>>>({});
  const setPriceInputRef = useMemo(
    () => ({
      tpTrigger: (el: HTMLInputElement | null) => void (priceInputRefs.current.tpTrigger = el),
      tpLimit: (el: HTMLInputElement | null) => void (priceInputRefs.current.tpLimit = el),
      slTrigger: (el: HTMLInputElement | null) => void (priceInputRefs.current.slTrigger = el),
      slLimit: (el: HTMLInputElement | null) => void (priceInputRefs.current.slLimit = el),
    }),
    [],
  );

  // 가격 입력만 Tab으로 순회한다: TP trigger → [TP limit] → SL trigger → [SL limit].
  const handlePriceInputTab = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return;
    const orderedInputs = PRICE_INPUT_ORDER.map((key) => priceInputRefs.current[key]).filter(
      (el): el is HTMLInputElement => el != null,
    );
    const index = orderedInputs.indexOf(e.target as HTMLInputElement);
    if (index === -1) return;
    const nextIndex = e.shiftKey ? index - 1 : index + 1;
    if (nextIndex < 0 || nextIndex >= orderedInputs.length) return;
    e.preventDefault();
    orderedInputs[nextIndex].focus();
  };

  return (
    <>
      <Modal.Header>
        <Modal.Title label={t('Position.r250401.Take_profit_stop_loss_title')} />
      </Modal.Header>
      <Modal.Content>
        <div className={vstack({ gap: 16, alignItems: 'stretch' })}>
          <Tabs
            type='fixed'
            size='large'
            value={draft.side}
            onChange={(side) =>
              dispatch({
                kind: 'switchSide',
                side,
                modes: readAdvancedOrderTpslBasisModes() ?? defaultAdvancedOrderTpslBasisModes,
              })
            }
          >
            <Tabs.Tab
              value='Long'
              label={t('Autogen_common.r251126.s_Long')}
              selectedColorSet={{ color: 'colors.content.accent.buy', borderColor: 'colors.border.accent.buy' }}
            />
            <Tabs.Tab
              value='Short'
              label={t('Autogen_common.r251126.s_Short')}
              selectedColorSet={{ color: 'colors.content.accent.sell', borderColor: 'colors.border.accent.sell' }}
            />
          </Tabs>

          <SymbolSummary side={draft.side} />

          {/* key={draft.side}: 탭 전환 시 입력 영역을 remount하여 직전 탭의 입력 표시값까지 확실히 초기화한다. */}
          <div key={draft.side} className={vstack({ gap: 16, alignItems: 'stretch' })} onKeyDown={handlePriceInputTab}>
            <TpSlOrderInput
              title={t('Autogen_common.r251126.s_Take_Profit')}
              legType='tp'
              side={draft.side}
              leg={draft.tp}
              precision={pricePrecision}
              quoteCurrency={quoteCurrency}
              entryRefPrice={entryRefPrice}
              amount={amount}
              leverage={leverage}
              triggerInputRef={setPriceInputRef.tpTrigger}
              limitInputRef={setPriceInputRef.tpLimit}
              onPatch={(patch) => dispatch({ kind: 'patchLeg', leg: 'tp', patch })}
            />
            <TpSlOrderInput
              title={t('Autogen_common.r251126.s_Stop_Loss')}
              legType='sl'
              side={draft.side}
              leg={draft.sl}
              precision={pricePrecision}
              quoteCurrency={quoteCurrency}
              entryRefPrice={entryRefPrice}
              amount={amount}
              leverage={leverage}
              triggerInputRef={setPriceInputRef.slTrigger}
              limitInputRef={setPriceInputRef.slLimit}
              onPatch={(patch) => dispatch({ kind: 'patchLeg', leg: 'sl', patch })}
            />
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Button
          size='large'
          label={t('Common.r250401.Confirm')}
          disabled={confirmDisabled}
          onClick={handleConfirm}
          css={{ w: '100%' }}
        />
      </Modal.Footer>
    </>
  );
};

export default AdvancedTpSlModal;

const SymbolSummary = ({ side }: { side: PresetSide }) => {
  const { symbol, symbolInfoAtom, referencePriceAtom } = useBunja(symbolBunja);
  const { orderStateAtom } = useBunja(formBunja);
  const { marginTypeAtom } = useMarginType();

  const { pricePrecision } = useAtomValue(symbolInfoAtom);
  const referencePrice = useAtomValue(referencePriceAtom);
  const { leverage } = useAtomValue(orderStateAtom);
  const marginType = useAtomValue(marginTypeAtom);

  return (
    <SymbolDisplay
      symbol={symbol}
      icon={<SymbolDisplay.Icon size={32} space={8} />}
      mainLabel={
        <SymbolDisplay.Label
          value='shortName'
          css={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            textStyle: 'Body/XL/Strong',
            color: 'content.default.level-5',
          }}
        >
          <div className={hstack({ gap: 4 })}>
            <OrderSideBadge orderSide={side} hideIcon />
            <LeverageBadge leverage={leverage} marginType={marginType} />
          </div>
        </SymbolDisplay.Label>
      }
      subLabel={
        <Formatted.Number
          value={referencePrice}
          precision={pricePrecision}
          toFixed
          css={{ textStyle: 'Body/M/Regular', color: 'content.default.level-2' }}
        />
      }
    />
  );
};

const TpSlOrderInput = ({
  title,
  legType,
  side,
  leg,
  precision,
  quoteCurrency,
  entryRefPrice,
  amount,
  leverage,
  triggerInputRef,
  limitInputRef,
  onPatch,
}: {
  title: string;
  legType: LegType;
  side: PresetSide;
  leg: TpSlLeg;
  precision: number;
  quoteCurrency: string;
  entryRefPrice: string;
  amount: string;
  leverage: number;
  /** Tab 순회용 ref — trigger/limit 가격 입력을 부모 레지스트리에 등록한다. */
  triggerInputRef?: Ref<HTMLInputElement>;
  limitInputRef?: Ref<HTMLInputElement>;
  onPatch: (patch: Partial<TpSlLeg>) => void;
}) => {
  const t = useTranslations();
  // 방향 에러 문구 기준: market 진입 → "market price", limit/trigger 진입 → "order price". (Don 결정)
  const { priceTypeAtom } = useBunja(formBunja);
  const isMarketEntry = useAtomValue(priceTypeAtom) === 'market';
  // trigger 입력이 있는데 방향이 잘못된 경우만 에러 표기 (execution type과 무관하게 trigger 방향만 검사).
  const directionInvalid =
    isLegActive(leg) && !isLegValid({ ...leg, executionType: 'market' }, { side, legType, anchor: entryRefPrice });
  const triggerAboveAnchor = (side === 'Long') === (legType === 'tp');
  const isLimitFixed = leg.executionType === 'limit' && !leg.isBbo;
  const ctx = buildOrderBasisCtx(side, entryRefPrice, amount, leverage);

  const directionErrorMessage = directionInvalid
    ? t(
        triggerAboveAnchor
          ? isMarketEntry
            ? 'Trade.r250401.Price_above_market_required_error'
            : 'Trade.r250401.Price_above_order_required_error'
          : isMarketEntry
            ? 'Trade.r250401.Price_below_market_required_error'
            : 'Trade.r250401.Price_below_order_required_error',
      )
    : undefined;

  const readTriggerState = (): PriceBasisState => ({
    triggerPrice: leg.triggerPrice,
    limitPrice: leg.limitPrice,
    secondaryValue: leg.triggerSecondary,
    basisMode: leg.triggerBasisMode,
    isLimitFixed,
  });

  const readLimitState = (): PriceBasisState => ({
    triggerPrice: leg.triggerPrice,
    limitPrice: leg.limitPrice,
    secondaryValue: leg.limitSecondary,
    basisMode: 'change',
    isLimitFixed: true,
  });

  const handleTriggerPriceChange = (nextTrigger: string) => {
    // Trigger-row secondary always tracks trigger price (dual-field).
    const next = syncOnTriggerPriceChange({ ...readTriggerState(), isLimitFixed: false }, nextTrigger, ctx);
    onPatch({
      triggerPrice: next.triggerPrice,
      triggerSecondary: formatSecondaryDisplay(next.secondaryValue),
    });
  };

  const handleTriggerSecondaryChange = (nextSecondary: string) => {
    // Trigger row only — never equalize Limit when Limit Order is on.
    const state = { ...readTriggerState(), isLimitFixed: false };
    const next = syncOnSecondaryChange(state, nextSecondary, ctx);
    onPatch({
      triggerSecondary: nextSecondary,
      triggerPrice:
        next.triggerPrice !== state.triggerPrice ? formatAbsolutePrice(next.triggerPrice, precision) : leg.triggerPrice,
    });
  };

  const handleTriggerBasisModeChange = (mode: ModalBasisMode) => {
    // Advanced context default is empty (not entry).
    const next = onBasisModeChange(readTriggerState(), mode, { triggerPriceDefault: '' });
    onPatch({
      triggerBasisMode: next.basisMode,
      triggerSecondary: next.secondaryValue,
      triggerPrice: next.triggerPrice,
    });
  };

  const handleLimitPriceChange = (nextLimit: string) => {
    const next = syncOnLimitPriceChange(readLimitState(), nextLimit, ctx);
    onPatch({
      limitBasisMode: 'change',
      limitPrice: next.limitPrice,
      limitSecondary: formatSecondaryDisplay(next.secondaryValue),
    });
  };

  const handleLimitSecondaryChange = (nextSecondary: string) => {
    // Limit row only — do not move Trigger price/secondary.
    const state = readLimitState();
    const next = syncOnSecondaryChange(state, nextSecondary, ctx);
    onPatch({
      limitBasisMode: 'change',
      limitSecondary: nextSecondary,
      limitPrice:
        next.limitPrice !== state.limitPrice ? formatAbsolutePrice(next.limitPrice, precision) : leg.limitPrice,
    });
  };

  return (
    <div className={vstack({ gap: 8, alignItems: 'stretch' })}>
      <div className={vstack({ gap: 12, alignItems: 'stretch' })}>
        <div className={vstack({ gap: 8, alignItems: 'stretch' })}>
          <div className={flex({ justify: 'space-between', align: 'center' })}>
            <span className={css({ textStyle: 'Body/M/Regular', color: 'content.default.level-5' })}>{title}</span>
            <Checkbox
              checked={leg.executionType === 'limit'}
              onChange={(e) =>
                onPatch({
                  executionType: e.target.checked ? 'limit' : 'market',
                  isBbo: false,
                  limitPrice: '',
                  limitSecondary: '',
                })
              }
              css={{ textStyle: 'Body/M/Regular', color: 'content.default.level-5' }}
            >
              {t('Autogen_common.r251126.s_Limit_Order')}
            </Checkbox>
          </div>
          <ModalPriceBasisRow
            priceValue={leg.triggerPrice}
            onPriceChange={handleTriggerPriceChange}
            pricePrecision={precision}
            pricePlaceholder={t('Autogen_common.r251126.s_Enter_price')}
            priceInputRef={triggerInputRef}
            triggerPriceType={leg.triggerPriceType}
            onTriggerPriceTypeChange={(triggerPriceType) => onPatch({ triggerPriceType })}
            showTriggerPriceTypeSelect={false}
            basisMode={leg.triggerBasisMode}
            onBasisModeChange={handleTriggerBasisModeChange}
            secondaryValue={leg.triggerSecondary}
            onSecondaryChange={handleTriggerSecondaryChange}
            quoteCurrency={quoteCurrency}
            leg={legType}
            side={side}
            showQuickPercents={false}
            errorMessage={directionErrorMessage}
          />
        </div>

        {leg.executionType === 'limit' && (
          <div className={vstack({ gap: 8, alignItems: 'stretch' })}>
            <div className={flex({ justify: 'space-between', align: 'center' })}>
              <span className={css({ textStyle: 'Body/M/Regular', color: 'content.default.level-5' })}>
                {t('Trade.r260420.Limit_price')}
              </span>
              <Checkbox
                checked={leg.isBbo}
                onChange={(e) =>
                  onPatch({
                    isBbo: e.target.checked,
                    limitPrice: '',
                    limitSecondary: '',
                  })
                }
                css={{ textStyle: 'Body/M/Regular', color: 'content.default.level-5' }}
              >
                {t('Autogen_common.r251126.s_BBO')}
              </Checkbox>
            </div>
            <ModalPriceBasisRow
              priceValue={leg.limitPrice}
              onPriceChange={handleLimitPriceChange}
              pricePrecision={precision}
              pricePlaceholder={t('Autogen_common.r251126.s_Enter_price')}
              priceInputRef={limitInputRef}
              // Limit has no triggerPriceType — hide Mark/Last.
              triggerPriceType={leg.triggerPriceType}
              onTriggerPriceTypeChange={() => {}}
              showTriggerPriceTypeSelect={false}
              // Limit secondary is Change(%) only — no ROI/PnL dropdown.
              basisMode='change'
              onBasisModeChange={() => {}}
              showBasisSelect={false}
              secondaryValue={leg.limitSecondary}
              onSecondaryChange={handleLimitSecondaryChange}
              quoteCurrency={quoteCurrency}
              leg={legType}
              side={side}
              showQuickPercents={false}
              isBbo={leg.isBbo}
              bboLabel={t('Trade.r251104.Best_counterparty')}
            />
          </div>
        )}
      </div>

      {leg.executionType === 'limit' && !leg.isBbo && (
        <OrderPnlHint
          mode='limit'
          side={side}
          entryPrice={entryRefPrice}
          exitPrice={leg.limitPrice}
          amount={amount}
          leverage={leverage}
          quoteCurrency={quoteCurrency}
        />
      )}

      {/* Market(트리거만) 주문도 trigger price가 입력되면 그 가격 기준 예상 P&L을 보여준다. */}
      {leg.executionType === 'market' && isLegActive(leg) && (
        <OrderPnlHint
          mode='market'
          side={side}
          entryPrice={entryRefPrice}
          exitPrice={leg.triggerPrice}
          amount={amount}
          leverage={leverage}
          quoteCurrency={quoteCurrency}
        />
      )}
    </div>
  );
};

/**
 * TP/SL leg의 예상 P&L 안내. Limit/Market 모두 UI는 동일하고, 기준가(exitPrice)와 안내 문구만 다르다.
 * entryPrice는 Advanced live entryRef (`calculationPriceAtom`)를 사용한다.
 */
const OrderPnlHint = ({
  mode,
  side,
  entryPrice,
  exitPrice,
  amount,
  leverage,
  quoteCurrency,
}: {
  mode: 'limit' | 'market';
  side: PresetSide;
  entryPrice: string;
  exitPrice: string;
  amount: string;
  leverage: number;
  quoteCurrency: string;
}) => {
  const hasPrice = !!exitPrice && !Num(exitPrice).isZero();
  const { estUnrealizedPnl, estUnrealizedPnlPct } = calcOrderEstUnrealizedPnl(
    side,
    entryPrice,
    exitPrice,
    amount,
    leverage,
  );

  // key: t.rich 가 placeholder 값을 배열 자식으로 합치므로 element 값에 key 부여(React key 경고 방지).
  const pnlValue = hasPrice ? (
    <Formatted.Number key='pnl' value={estUnrealizedPnl} precision={2} toFixed withColor withPlus />
  ) : (
    '--'
  );
  const roiValue = hasPrice ? (
    <Formatted.Number key='roi' value={estUnrealizedPnlPct} precision={2} toFixed withColor withPlus />
  ) : (
    '--'
  );

  return (
    <p className={css({ textStyle: 'Body/S/Regular', color: 'content.default.level-1' })}>
      {mode === 'limit' ? (
        <>
          <FormattedMessage
            id='Trade.r260615.Estimated_pnl_will_be_symbol'
            values={{ pnl: pnlValue, symbol: quoteCurrency }}
          />{' '}
          <FormattedMessage
            id='Trade.r260615.Roi_colon_percent'
            values={{
              percent: hasPrice ? <span key='roi-percent'>{roiValue}%</span> : '--%',
            }}
          />
        </>
      ) : (
        <FormattedMessage
          id='Trade.r260611.Placed_as_a_market_order_when_triggered_estimated_pnl_will_be_pnl_usdt_roi_roi_percentage'
          values={{ pnl: pnlValue, roi: roiValue }}
        />
      )}
    </p>
  );
};
